<template>
  <div class="change-password-container">
    <div class="change-password-card">
      <h1>Wachtwoord wijzigen</h1>

      <form @submit.prevent="handleSubmit" class="change-password-form">
        <div class="form-group">
          <label for="current-password">Huidig wachtwoord</label>
          <input type="password" id="current-password" v-model="currentPassword" required />
        </div>

        <div class="form-group">
          <label for="new-password">Nieuw wachtwoord</label>
          <input type="password" id="new-password" v-model="newPassword" required />
        </div>

        <div class="form-group">
          <label for="confirm-password">Bevestig nieuw wachtwoord</label>
          <input type="password" id="confirm-password" v-model="confirmPassword" required />
        </div>

        <div v-if="error" class="error-message">
          {{ error }}
        </div>

        <div v-if="success" class="success-message">
          {{ success }}
        </div>

        <button type="submit" :disabled="loading" class="submit-button">
          {{ loading ? 'Bezig met wijzigen...' : 'Wachtwoord wijzigen' }}
        </button>
      </form>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { ref } from 'vue'
  import { useRouter } from 'vue-router'
  import { supabase } from '../lib/supabase'

  const router = useRouter()
  const currentPassword = ref('')
  const newPassword = ref('')
  const confirmPassword = ref('')
  const error = ref('')
  const success = ref('')
  const loading = ref(false)

  const handleSubmit = async () => {
    error.value = ''
    success.value = ''
    loading.value = true

    try {
      // Validate passwords match
      if (newPassword.value !== confirmPassword.value) {
        throw new Error('Nieuwe wachtwoorden komen niet overeen')
      }

      // Get current session
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) {
        throw new Error('Geen actieve sessie gevonden')
      }

      // Update password
      const { error: updateError } = await supabase.auth.updateUser({
        password: newPassword.value
      })

      if (updateError) throw updateError

      success.value = 'Wachtwoord succesvol gewijzigd'

      // Clear form
      currentPassword.value = ''
      newPassword.value = ''
      confirmPassword.value = ''

      // Redirect after 2 seconds
      setTimeout(() => {
        router.push('/')
      }, 2000)

    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Er is een onbekende fout opgetreden'
    } finally {
      loading.value = false
    }
  }
</script>

<style scoped>
  .change-password-container {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 100vh;
    padding: 20px;
    background-color: #f3f4f6;
  }

  .change-password-card {
    background: white;
    padding: 2rem;
    border-radius: 8px;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
    width: 100%;
    max-width: 400px;
  }

  h1 {
    text-align: center;
    color: #111827;
    margin-bottom: 2rem;
    font-size: 1.5rem;
    font-weight: 600;
  }

  .change-password-form {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
  }

  .form-group {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  label {
    font-size: 0.875rem;
    font-weight: 500;
    color: #374151;
  }

  input {
    padding: 0.5rem 0.75rem;
    border: 1px solid #d1d5db;
    border-radius: 4px;
    font-size: 0.875rem;
  }

  input:focus {
    outline: none;
    border-color: #4F46E5;
    box-shadow: 0 0 0 2px rgba(79, 70, 229, 0.2);
  }

  .error-message {
    color: #dc2626;
    font-size: 0.875rem;
    text-align: center;
  }

  .success-message {
    color: #059669;
    font-size: 0.875rem;
    text-align: center;
  }

  .submit-button {
    background-color: #4F46E5;
    color: white;
    padding: 0.75rem 1rem;
    border-radius: 4px;
    font-weight: 500;
    border: none;
    cursor: pointer;
    transition: background-color 0.2s;
  }

  .submit-button:hover {
    background-color: #4338ca;
  }

  .submit-button:disabled {
    background-color: #6b7280;
    cursor: not-allowed;
  }
</style>
